/**
 * SMS provider settings hooks (#3023, Elisa Dialogi). Types live here, as for
 * the other integrations; the contract is `routes/integrations-sms.ts` in
 * staff-api.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type SmsSenderType = 'MSISDN' | 'NATIONAL' | 'ALNUM';

export interface SmsLastTest {
  testedAt: string;
  success: boolean;
  /** Stable code, e.g. `SMS_CHECK_SENDER_NOT_CONFIRMED`. */
  error?: string;
}

export interface SmsCredentialsStatus {
  enabled: boolean;
  apiOrigin: string | null;
  /** False when the stored origin is not (or no longer) one the operator allows. */
  apiOriginAllowed: boolean;
  /** The origins the operator allows for this service. */
  allowedApiOrigins: string[];
  /** The API key is never returned. */
  hasApiKey: boolean;
  hasCallbackToken: boolean;
  callbackTokenRotatedAt: string | null;
  encryptionAvailable: boolean;
  senderType: SmsSenderType | null;
  sender: string | null;
  /** The workshop confirmed the sender is registered with Traficom or authorized in the service. */
  senderAuthorized: boolean;
  receivingNumber: string | null;
  /** The workshop confirmed the reply route exists in the Dialogi message router. */
  receivingRouteVerified: boolean;
  /** STOP and yes/no replies can be received. */
  repliesEnabled: boolean;
  requiresOptOutLink: boolean;
  /** Result of the LOCAL configuration check: no request is made to Elisa. */
  lastTest: SmsLastTest | null;
  /** Complete, confirmed, checked and switched on. */
  ready: boolean;
}

export type SmsAttentionDecision = 'not_sent' | 'sent' | 'abandon' | 'acknowledge';

export interface SmsAttentionItem {
  id: string;
  purpose: string;
  /** An unknown outcome (a person decides) or a created message whose sender was converted (acknowledge). */
  kind: 'unknown_outcome' | 'sender_warning';
  /** Masked: `***567`. */
  recipient: string;
  reason: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A work order (and offer) to open for an unresolved reply: a lead, never a decision. */
export interface SmsUnresolvedCandidate {
  bookingId: string;
  workOrderNumber: string | null;
  customerName: string | null;
  offerId: string | null;
  offerTitle: string | null;
  /** `question`: a yes/no question asked of this number about it. `message`: a text sent to this number about it. */
  via: 'question' | 'message';
}

export interface SmsUnresolvedReply {
  id: string;
  /** `unresolved_*`: why the reply was not applied. */
  outcome: string;
  textClass: string;
  /** Masked: `***567`. The full number is `useSmsReplyContact`, behind the customer permission. */
  sender: string | null;
  receivedAt: string;
  /** Where to look to follow it up; empty when nothing links the number to a work order. */
  candidates: SmsUnresolvedCandidate[];
}

/** The full number of an unresolved reply and the customers on file with it. */
export interface SmsReplyContact {
  sender: string | null;
  customers: { partyType: 'person' | 'company'; partyId: string; name: string }[];
}

/** What is known about whether one person of a work order uses WhatsApp (who an SMS would reach). */
export interface SmsRecipientState {
  partyType: 'person' | 'company';
  partyId: string;
  name: string;
  roles: string[];
  /** The number an SMS would go to, masked; null without a usable international number. */
  smsDestinationMasked: string | null;
  /** `confirmed_absent`: a person recorded that they do not use WhatsApp, for these numbers. `stale`: the numbers changed since. */
  whatsapp: 'unconfirmed' | 'confirmed_absent' | 'stale';
  /** The customer turned notifications off: no confirmation overrides that. */
  optedOut: boolean;
  recordedAt: string | null;
  recordedBy: string | null;
}

export interface SmsIntegrationResponse {
  credentials: SmsCredentialsStatus;
  publicUrlConfigured: boolean;
  /** The tenant's Customer Portal address booking texts link to (operator-configured); null: booking texts cannot be sent. */
  customerPortalOrigin: string | null;
  /** The inbound (MO) target URL WITHOUT its secret; the full one is shown once after saving. */
  inboundBaseUrl: string | null;
  attentionCount: number;
  attention: SmsAttentionItem[];
  unresolvedReplyCount: number;
  unresolvedReplies: SmsUnresolvedReply[];
}

export interface SmsCredentialsInput {
  apiOrigin: string;
  /** Omit to keep the stored key. */
  apiKey?: string;
  senderType: SmsSenderType;
  sender: string;
  senderAuthorized: boolean;
  receivingNumber?: string;
  receivingRouteVerified: boolean;
  /** Generate a new callback address; the old one still works for delivery reports for a day. */
  rotateCallbackToken?: boolean;
}

/** The saved status, plus the inbound URL carrying the new secret when one was generated: shown once. */
export interface SmsSaveResponse extends SmsCredentialsStatus {
  inboundUrlOnce: string | null;
}

export interface SmsTestResponse {
  success: boolean;
  credentials: SmsCredentialsStatus;
}

const SMS_QUERY_KEY = ['integrations', 'sms'] as const;

export function useSmsIntegration(enabled: boolean) {
  return useQuery({
    queryKey: SMS_QUERY_KEY,
    queryFn: () => api.get<SmsIntegrationResponse>('/integrations/sms'),
    enabled,
  });
}

/** For the work-order screens: can an SMS be sent at all, and may it ask a yes/no question. Needs only a work-order permission. */
export function useSmsStatus(enabled = true) {
  return useQuery({
    queryKey: [...SMS_QUERY_KEY, 'status'],
    queryFn: () => api.get<{ enabled: boolean; repliesEnabled: boolean }>('/integrations/sms/status'),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

function useSmsMutation<TInput, TResult>(mutationFn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SMS_QUERY_KEY });
    },
  });
}

export function useSaveSmsCredentials() {
  return useSmsMutation((input: SmsCredentialsInput) => api.put<SmsSaveResponse>('/integrations/sms/credentials', input));
}

export function useTestSmsCredentials() {
  return useSmsMutation(() => api.post<SmsTestResponse>('/integrations/sms/test', {}));
}

export function useSetSmsEnabled() {
  return useSmsMutation((enabled: boolean) => api.put<SmsCredentialsStatus>('/integrations/sms/enabled', { enabled }));
}

export function useDeleteSmsCredentials() {
  return useSmsMutation(() => api.delete<SmsCredentialsStatus>('/integrations/sms/credentials'));
}

export function useResolveSmsMessage() {
  return useSmsMutation((input: { id: string; decision: SmsAttentionDecision }) =>
    api.post<{ id: string; status: string }>(`/integrations/sms/messages/${encodeURIComponent(input.id)}/resolve`, {
      decision: input.decision,
    }),
  );
}

export function useDismissSmsReply() {
  return useSmsMutation((id: string) =>
    api.post<{ id: string; dismissed: boolean }>(`/integrations/sms/replies/${encodeURIComponent(id)}/dismiss`, {}),
  );
}

const recipientsKey = (workOrderId: string) => [...SMS_QUERY_KEY, 'recipients', workOrderId] as const;

/** Who an SMS from this work order would reach, and what a person has recorded about their WhatsApp use. */
export function useSmsRecipients(workOrderId: string, enabled = true) {
  return useQuery({
    queryKey: recipientsKey(workOrderId),
    queryFn: () => api.get<{ recipients: SmsRecipientState[] }>(`/integrations/sms/work-orders/${encodeURIComponent(workOrderId)}/recipients`),
    enabled,
    retry: false,
  });
}

function recipientPath(workOrderId: string, partyType: string, partyId: string): string {
  return `/integrations/sms/work-orders/${encodeURIComponent(workOrderId)}/recipients/${partyType}/${encodeURIComponent(partyId)}/no-whatsapp`;
}

interface RecipientTarget {
  partyType: 'person' | 'company';
  partyId: string;
}

/** Record that this person does not use WhatsApp: the only way a direct text message to them is allowed. */
export function useRecordNoWhatsApp(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (target: RecipientTarget) =>
      api.put<{ recorded: boolean; created: boolean; recipients: SmsRecipientState[] }>(recipientPath(workOrderId, target.partyType, target.partyId), {}),
    onSuccess: (result) => {
      queryClient.setQueryData(recipientsKey(workOrderId), { recipients: result.recipients });
    },
  });
}

/** Withdraw that confirmation. */
export function useClearNoWhatsApp(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (target: RecipientTarget) =>
      api.delete<{ cleared: boolean; recipients: SmsRecipientState[] }>(recipientPath(workOrderId, target.partyType, target.partyId)),
    onSuccess: (result) => {
      queryClient.setQueryData(recipientsKey(workOrderId), { recipients: result.recipients });
    },
  });
}

/** The full number of an unresolved reply, on demand. Needs the customer permission; never cached. */
export function useSmsReplyContact() {
  return useMutation({
    mutationFn: (replyId: string) => api.get<SmsReplyContact>(`/integrations/sms/replies/${encodeURIComponent(replyId)}/contact`),
    gcTime: 0,
  });
}
